import type { FeatureCollection } from "@turf/helpers";
import type { Map } from "mapbox-gl";

export function esriToGeoJSON(geoJSON: Record<string, any>): FeatureCollection {
  const convertedFeatures = geoJSON.features.map((feature) => {
    const coordinates = feature.geometry;
    const properties = feature.attributes;

    const finalCoordinates = coordinates.x
      ? [coordinates.x, coordinates.y]
      : coordinates?.rings && coordinates?.rings[0];

    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: finalCoordinates,
      },
      properties,
    };
  });

  return {
    type: "FeatureCollection",
    features: convertedFeatures,
  };
}

function moveLayerSafely(map, sourceLayerId, targetLayerId) {
  if (map.getLayer(sourceLayerId) && map.getLayer(targetLayerId)) {
    map.moveLayer(sourceLayerId, targetLayerId);
  } else if (map.getLayer(sourceLayerId)) {
    // Move to the highest layer if target layer does not exist
    const { layers } = map.getStyle();
    const highestLayerId = layers[layers.length - 1].id;
    map.moveLayer(sourceLayerId, highestLayerId);
  }
}

export const setGeoJSONLayer = (
  map: Map,
  types = [
    "Point",
    "LineString",
    "MultiLineString",
    "Polygon",
    "MultiPolygon",
  ] as string[],
) => {
  const t = [...new Set(types)];
  const id = "gisData";

  map.addSource(id, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [],
    },
  });

  const addLayerWithSelected = (layerConfig) => {
    // Add regular layer
    map.addLayer(layerConfig);

    // Add selected variant
    const selectedId = [...layerConfig.id.split("-"), "selected"].join("-");
    const selectedConfig = {
      ...layerConfig,
      id: selectedId,
      layout: {
        ...layerConfig.layout,
        visibility: "none",
      },
    };

    // For Point layers, set circle-radius to 0 to hide vertices
    if (selectedConfig.type === "circle") {
      selectedConfig.paint = {
        ...selectedConfig.paint,
        "circle-radius": 0,
      };
    }

    if (
      selectedConfig.id.includes("LineString") ||
      selectedConfig.id.includes("MultiLineString")
    ) {
      selectedConfig.paint = {
        ...selectedConfig.paint,
        "line-width": 4,
      };
    }

    map.addLayer(selectedConfig);
  };

  t.forEach((type) => {
    if (type === "Polygon" || type === "MultiPolygon") {
      const filterCondition = type === "MultiPolygon" ? "Polygon" : type;

      addLayerWithSelected({
        id: [id, type, "outline"].join("-"),
        source: id,
        type: "line",
        paint: {
          "line-color": [
            "coalesce",
            ["get", "borderColor"],
            ["get", "color"],
            "#CD992E",
          ],
          "line-width": ["coalesce", ["get", "borderWidth"], 2],
          "line-opacity": ["coalesce", ["get", "opacity"], 1],
        },
        filter: ["==", "$type", filterCondition],
      });
      map.addLayer({
        id: [id, type, "outline", "click-clone"].join("-"),
        source: id,
        type: "line",
        paint: {
          "line-color": "#000",
          "line-opacity": 0,
          "line-width": 6, // Wider transparent line for better clickability
        },
        filter: ["==", "$type", filterCondition], // Only display LineString features
      });

      addLayerWithSelected({
        id: [id, type, "fill"].join("-"),
        source: id,
        type: "fill",
        paint: {
          "fill-color": ["coalesce", ["get", "color"], "#fed957"],
          "fill-opacity": [
            "case",
            ["has", "ideaId"],
            0,
            ["coalesce", ["get", "opacity"], 0.4],
          ],
        },
        filter: ["==", "$type", filterCondition],
      });
    }

    if (type === "Point") {
      addLayerWithSelected({
        id: [id, type].join("-"),
        source: id,
        type: "circle",
        paint: {
          "circle-color": ["coalesce", ["get", "color"], "#fed062"],
          "circle-opacity": ["coalesce", ["get", "opacity"], 1],
          "circle-stroke-opacity": ["coalesce", ["get", "opacity"], 1],
          "circle-stroke-color": "#fff",
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            ["*", 0.1, ["get", "circleRadius"]],
            5,
            ["*", 0.7, ["get", "circleRadius"]],
            10,
            ["*", 0.8, ["get", "circleRadius"]],
            12,
            ["*", 0.8, ["get", "circleRadius"]],
            14,
            ["*", 1.5, ["get", "circleRadius"]],
            20,
            ["*", 1.2, ["get", "circleRadius"]],
          ],
          "circle-stroke-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            0.1,
            10,
            0.4,
            20,
            3,
          ],
        },
        filter: ["==", "$type", "Point"],
      });
    }

    if (type === "LineString" || type === "MultiLineString") {
      const filterCondition = type === "MultiLineString" ? "LineString" : type;

      addLayerWithSelected({
        id: [id, type].join("-"),
        source: id,
        type: "line",
        paint: {
          "line-color": ["coalesce", ["get", "color"], "#CD992E"],
          "line-opacity": ["coalesce", ["get", "opacity"], 1],
          "line-width": 2,
        },
        filter: ["==", "$type", filterCondition],
      });

      map.addLayer({
        id: [id, type, "click-clone"].join("-"),
        source: id,
        type: "line",
        paint: {
          "line-color": "#000",
          "line-opacity": 0,
          "line-width": 6, // Wider transparent line for better clickability
        },
        filter: ["==", "$type", filterCondition], // Only display LineString features
      });
    }
  });

  requestAnimationFrame(() => {
    map?.getStyle()?.layers?.forEach(({ id }) => {
      // console.log(id);
      if (id.startsWith("gisData")) {
        moveLayerSafely(map, id, "gisLabels");

        moveLayerSafely(map, "gisData-Polygon", "gisLabels");
        moveLayerSafely(map, "gisData-Polygon-outline", "gisLabels");
        moveLayerSafely(
          map,
          "gisData-Polygon-outline-click-clone",
          "gisLabels",
        );

        moveLayerSafely(map, "gisData-MultiPolygon", "gisLabels");
        moveLayerSafely(map, "gisData-MultiPolygon-outline", "gisLabels");
        moveLayerSafely(
          map,
          "gisData-MultiPolygon-outline-click-clone",
          "gisLabels",
        );

        moveLayerSafely(map, "gisData-LineString", "gisLabels");
        moveLayerSafely(map, "gisData-LineString-click-clone", "gisLabels");

        moveLayerSafely(map, "gisData-MultiLineString", "gisLabels");
        moveLayerSafely(
          map,
          "gisData-MultiLineString-click-clone",
          "gisLabels",
        );

        moveLayerSafely(map, "gisData-Point", "gisLabels");

        // moveLayerSafely(map, "icon-layer-bg", "gisLabels");
        // moveLayerSafely(map, "icon-layer-icon", "gisLabels");
      }
    });
  });
};
